import styled, { css } from 'styled-components';
import colors from 'styles/colors';
import beemLogo from 'assets/logo_big.png';
import WeeveLogo from 'assets/Weeve-W-Icon.png';
import AvigoLogo from 'assets/Avigo-Logo-Icon.png';
import { Link } from 'react-router-dom';
import {
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownContent,
  Container as DropdownContainer,
} from 'components/DropdownMenu/DropdownMenu.styles';
import { CollapsibleListBody } from 'components/CollapsibleList';
import { fontWeight, MENU_HEIGHT, Tag } from 'styles/common';
import { BrandingTypes } from 'services/resources';

const logoMap: Record<BrandingTypes, string> = {
  [BrandingTypes.FLEETLANE]: beemLogo,
  [BrandingTypes.WEEVE]: WeeveLogo,
  [BrandingTypes.AVIGO]: AvigoLogo,
};

export const ThirdPartySupportTextContainer = styled.div`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  font-weight: ${fontWeight.semibold};
  font-size: 14px;
  color: ${colors.gray55};

  & > div {
    display: flex;
    gap: 8px;
  }

  & a {
    font-size: 12px;

    &:nth-of-type(1) {
      font-size: 14px;
    }
    color: ${colors.gray55};
    &:active {
      color: ${colors.gray30};
    }

    &:hover {
      color: ${colors.gray70};
    }
  }
`;

const SUB_MENU_ITEM_MIXIN = css`
  color: ${colors.gray55};
  text-decoration: none;
  margin-left: 25px;
  transition: text-shadow 0.3s;
  font-size: 13px;
  font-weight: ${fontWeight.semibold};
  transition: 0.15s color ease-in-out;
  text-transform: uppercase;
  padding: 0;

  &:hover {
    color: ${colors.primary};
  }
`;

export const MenuContainer = styled.header`
  background-color: ${colors.white};
  border-bottom: 1px solid ${colors.gray75};
  position: fixed;
  left: 0;
  width: 100%;
  height: ${MENU_HEIGHT};
  z-index: 10;
`;

export const MenuBar = styled.nav`
  position: relative;
  align-items: center;
  display: flex;
  height: 100%;
  line-height: 18px;
  padding: 10px 20px;
`;

export const MainMenuLogo = styled(Link)`
  margin-right: 5px;
`;

export const MainMenuLogoIcon = styled.div<{ brandingType: BrandingTypes }>`
  width: 25px;
  height: 25px;
  background-image: url(${(props) => logoMap[props.brandingType]});
  background-size: cover;
  border-radius: 4px;
`;

export const Navigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export const NavItems = styled.div`
  display: flex;
  align-items: center;

  ${DropdownContainer} {
    height: 40px;
    display: flex;
  }

  ${DropdownContent} {
    ${SUB_MENU_ITEM_MIXIN};
    margin-left: 0;
  }
`;

export const SecondaryNavItems = styled(NavItems)`
  button {
    padding-right: 0;
  }
`;

export const SubmenuContainer = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
`;

export const DropdownContentWrapper = styled.div`
  ${DropdownMenuContent} {
    margin-top: 2px;
  }
  ${DropdownMenuItem} {
    width: 140px;
  }
`;

export const SubmenuLinkItem = styled.div`
  & > a {
    ${SUB_MENU_ITEM_MIXIN}
  }
`;

export const SubmenuItem = styled.div`
  ${SUB_MENU_ITEM_MIXIN};
`;

export const IconItem = styled.div`
  color: ${colors.primary};
  margin-left: 20px;
  font-size: 30px;
`;

export const IconItemButton = styled.a`
  align-content: center;
  align-items: center;
  background-color: ${colors.gray70};
  border-radius: 50%;
  color: white;
  display: flex;
  flex-direction: row;
  font-size: 13px;
  height: 30px;
  justify-content: center;
  letter-spacing: 1px;
  margin-left: 20px;
  width: 30px;
  font-weight: ${fontWeight.semibold};

  &:hover {
    background-color: ${colors.primary};
    cursor: pointer;
  }
`;

export const Drawer = styled.div`
  background-color: ${colors.white};
  box-shadow: 0 2px 8px 0 rgba(160, 160, 160, 0.5);
  font-size: 0.875em;
  letter-spacing: 0.5px;
  height: 100vh;
  padding: 1em 0 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 300px;
  z-index: 9999;

  display: flex;
  flex-direction: column;

  ${CollapsibleListBody} {
    & > * {
      padding-left: 2em;
      padding-right: 2em;
    }
  }

  & ${ThirdPartySupportTextContainer} {
    padding: 0 12px 40px 12px;

    flex-direction: column;
    align-items: start;
    justify-content: center;
    gap: 2px;

    & div {
      flex-direction: column;
      align-items: start;
      gap: 6px;
    }
  }
`;

export const DrawerHead = styled.div`
  align-items: center;
  border-bottom: 0.75px solid ${colors.gray85};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0 14px 10px;
`;

export const DrawerTitle = styled.div`
  font-weight: ${fontWeight.semibold};
  line-height: 16px;
`;

export const DrawerBody = styled.div`
  padding: 1em 0 0;
  flex: 1 1 auto;
  overflow-y: auto;
`;

export const DrawerSubtitle = styled.div`
  font-size: 0.875em;
  line-height: 1.4em;
  letter-spacing: 0.5px;
`;

export const DrawerFooter = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  padding: 10px 14px;
  border-top: 0.75px solid ${colors.gray85};
  text-align: right;
`;

export const LinkContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  align-items: center;
`;

export const LinkPill = styled(Tag)``;
